// Service endpoints for the browser client — one place that answers "which
// server / broker do we talk to?" per environment, with no runtime host-sniffing.
//
// Resolution order for each service:
//   1. a `?server=` / `?broker=` URL param (a ws/wss URL) — a per-tab override
//      for edge cases: a dev browser hitting the prod backend, a custom or
//      staging server, or a friend's dev broker relayed along in a share link;
//   2. the build-time value Vite inlined from the mode's env file
//      (`.env.development` for `vite dev`, `.env.production` for `vite build`).
//      A dev (loopback) config follows THIS page's hostname so it works from
//      localhost AND from a LAN device that loaded the page off the host's IP
//      (LAN play); a prod config is a real domain, used verbatim;
//   3. a hardcoded first-party prod URL as a last resort, so a missing env var
//      degrades to production rather than to a dead localhost.
//
// The join link a host shares is same-origin (origin + pathname) so it works
// identically on the deployed player and on localhost — the friend lands on the
// same page with a ?join=<sessionId> that flips the emulator into "connect to
// this host over WebRTC" instead of "connect to the default server". Because
// it's same-origin, the joiner resolves the same env as the host, so a plain
// localhost session needs no broker param (both derive ws://localhost:48090).

const SERVER_PARAM = "server";
const BROKER_PARAM = "broker";
const JOIN_PARAM = "join";

// First-party production endpoints — the last-resort fallback when the build-time
// env var is absent, so the deploy fails toward prod, never toward a localhost
// that isn't listening.
const PROD_SERVER_URL = "wss://server.multinostalgia.net";
const PROD_BROKER_URL = "wss://connect.multinostalgia.net";

// A ws/wss override from a URL param, or null. A non-WebSocket value (e.g. an
// http:// string) is ignored, not honored.
function wsParam(loc: Location, name: string): string | null {
  const v = new URLSearchParams(loc.search).get(name);
  return v && /^wss?:\/\//.test(v) ? v : null;
}

// Whether a URL's host is loopback — i.e. a DEV config ("the service is on THIS
// machine"). Prod configs point at a real domain, so this is false for them.
function isLoopbackHost(url: string): boolean {
  try {
    const h = new URL(url).hostname;
    return h === "localhost" || h === "127.0.0.1" || h === "[::1]" || h === "::1";
  } catch { return false; }
}

// Rewrite a URL's host, keeping its scheme + port. Used to point a dev service
// at the host that actually served this page (see resolveEndpoint).
function withHost(url: string, hostname: string): string {
  try {
    const u = new URL(url);
    u.hostname = hostname;
    return u.toString().replace(/\/$/, ""); // ws URLs carry no path here
  } catch { return url; }
}

// Resolve one service URL: param override → build-time env → prod fallback.
//
// LAN support: when the env config is a LOOPBACK URL (dev), the service runs on
// the same machine that served this page, so we follow THIS page's hostname —
// `localhost` when you loaded it locally, the host's LAN IP (e.g. 192.168.1.x)
// when a phone/second device on the network loaded it. That makes a LAN device
// reach the host instead of its own localhost, with zero per-device config. A
// prod config points at a real remote domain, so it's used verbatim.
function resolveEndpoint(
  loc: Location, param: string, configured: string | undefined, prod: string,
): string {
  const override = wsParam(loc, param);
  if (override) return override;
  if (configured && /^wss?:\/\//.test(configured)) {
    return isLoopbackHost(configured) ? withHost(configured, loc.hostname) : configured;
  }
  return prod;
}

// The game server the player connects to (data plane + script-source fetch).
export function resolveServerUrl(loc: Location = window.location): string {
  return resolveEndpoint(loc, SERVER_PARAM, import.meta.env.VITE_SERVER_URL, PROD_SERVER_URL);
}

// The P2P signaling broker for friend-hosted sessions (docs/p2p.md).
export function resolveBrokerUrl(loc: Location = window.location): string {
  return resolveEndpoint(loc, BROKER_PARAM, import.meta.env.VITE_BROKER_URL, PROD_BROKER_URL);
}

// The session id from a join link, or null when this isn't a join (the normal
// default-server flow).
export function parseJoinSession(loc: Location = window.location): string | null {
  const v = new URLSearchParams(loc.search).get(JOIN_PARAM);
  return v !== null && v.length > 0 ? v : null;
}

// Build the shareable link: same origin + path as the host's page, carrying just
// the session id (and the broker override if one is in effect, so a dev-broker
// session stays reachable for the joiner). The mod list is deliberately NOT in
// the link — the joiner asks the host what it's running over P2P once connected
// (session-info.ts), which keeps the link short and the host the source of truth.
export function buildJoinUrl(
  sessionId: string,
  loc: Location = window.location,
): string {
  const url = new URL(loc.origin + loc.pathname);
  url.searchParams.set(JOIN_PARAM, sessionId);
  const broker = new URLSearchParams(loc.search).get(BROKER_PARAM);
  if (broker !== null && broker.length > 0) url.searchParams.set(BROKER_PARAM, broker);
  return url.toString();
}
